import { useState } from 'react';
import { clsx } from '../../../ds/clsx';
import { Button, Eyebrow, Pill, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { createDeveloperLink, sendDeveloperLink } from '../api';
import { shortDateTime } from '../format';
import type { DeveloperLinkState } from '../types';
import styles from './wizard.module.css';

export interface DeveloperLinkCardProps {
  propertyId: string;
  link: DeveloperLinkState;
  /** `uploads` = Objekt anlegen 02 („Upload-Link für den Bauträger"), `checklist` = 06/07 („Bauträger-Upload-Link"). */
  variant: 'uploads' | 'checklist';
  onChanged: () => void;
}

function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

function firstWord(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** The developer's upload link: create, copy, send by e-mail — `POST /properties/:id/developer-link[/send]`. */
export function DeveloperLinkCard({ propertyId, link, variant, onChanged }: DeveloperLinkCardProps) {
  const { toast } = useToast();
  const [busy, setBusy] = useState<'create' | 'send' | null>(null);
  const developerName = link.developer.name;
  /** One link per developer: an existing link shows nothing of this property until its requests exist. */
  const needsActivation = link.url !== null && link.openRequests + link.deliveredRequests === 0;

  async function create() {
    setBusy('create');
    try {
      await createDeveloperLink(propertyId);
      toast({ message: 'Der Upload-Link ist erzeugt.' });
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  async function send() {
    setBusy('send');
    try {
      const result = await sendDeveloperLink(propertyId);
      toast({ message: `Der Link wurde an ${result.sentTo} gesendet.` });
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  async function copy() {
    if (!link.url) return;
    toast({
      message: (await copyText(link.url)) ? 'Link kopiert.' : 'Der Link konnte nicht kopiert werden.',
    });
  }

  if (variant === 'uploads') {
    return (
      <section className={styles.card} aria-labelledby="wizard-developer-link">
        <Eyebrow id="wizard-developer-link">Upload-Link für den Bauträger</Eyebrow>
        <div className={styles.sideText}>{developerName} lädt fehlende Unterlagen selbst hoch.</div>
        {link.url ? (
          <div className={styles.linkField}>
            <span className={styles.linkValue} title={link.url}>
              {displayUrl(link.url)}
            </span>
            <button
              type="button"
              className={clsx('pressable', styles.smallButton)}
              onClick={() => void copy()}
            >
              Kopieren
            </button>
          </div>
        ) : (
          <div className={styles.sideStack}>
            <Button variant="secondary" loading={busy === 'create'} onClick={() => void create()}>
              Link erzeugen
            </Button>
          </div>
        )}
        <div className={styles.sideStack}>
          {needsActivation ? (
            <Button
              variant="secondary"
              loading={busy === 'create'}
              disabled={busy === 'send'}
              onClick={() => void create()}
            >
              Link für dieses Objekt aktivieren
            </Button>
          ) : null}
          <Button
            className={styles.primaryButton}
            loading={busy === 'send'}
            disabled={busy === 'create'}
            onClick={() => void send()}
          >
            Per E-Mail an {firstWord(developerName)} senden
          </Button>
        </div>
        <div className={styles.sideNote}>
          Gültig, bis alles vorliegt. Wird wieder aktiv, sobald Sie Neues anfordern. Der Bauträger sieht, was
          noch fehlt.
        </div>
      </section>
    );
  }

  return (
    <section className={styles.card} aria-labelledby="wizard-developer-link">
      <Eyebrow id="wizard-developer-link">Bauträger-Upload-Link</Eyebrow>
      <div className={styles.pillRow}>
        {link.url === null ? (
          <Pill variant="neutral">kein Link</Pill>
        ) : link.expired ? (
          <Pill variant="warning">abgelaufen</Pill>
        ) : (
          <Pill variant="success">aktiv</Pill>
        )}
        {link.lastSentAt ? (
          <span className={styles.pillRowText}>zuletzt gesendet {shortDateTime(link.lastSentAt)}</span>
        ) : null}
      </div>
      <div className={clsx(styles.sideText, styles.sideTextSmall)}>{link.summary}</div>
      <div className={styles.sideButtons}>
        {link.url === null ? (
          <button
            type="button"
            className={clsx('pressable', styles.smallButton)}
            disabled={busy !== null}
            onClick={() => void create()}
          >
            Link erzeugen
          </button>
        ) : (
          <>
            {needsActivation ? (
              <button
                type="button"
                className={clsx('pressable', styles.smallButton)}
                disabled={busy !== null}
                aria-busy={busy === 'create' || undefined}
                onClick={() => void create()}
              >
                Link für dieses Objekt aktivieren
              </button>
            ) : null}
            <button
              type="button"
              className={clsx('pressable', styles.smallButton)}
              disabled={busy !== null}
              aria-busy={busy === 'send' || undefined}
              onClick={() => void send()}
            >
              Erinnerung senden
            </button>
            <button
              type="button"
              className={clsx('pressable', styles.smallButton)}
              onClick={() => void copy()}
            >
              Link kopieren
            </button>
          </>
        )}
      </div>
    </section>
  );
}
